/**
 * DonorGrants.jsx — Phase 2
 * Grant records with embedded KPI arrays, progress tracking and at-risk alerts.
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  getDonorGrants, createDonorGrant, updateDonorGrant, deleteDonorGrant, updateKpi,
  getDonors, GRANT_STATUSES, calcKpiProgress, kpiRiskLevel, daysUntil, fmtDate,
} from '../../../api/donorsApi';

// ── KPI progress bar ──────────────────────────────────────────────────────────
function KpiBar({ kpi }) {
  const pct = calcKpiProgress(kpi);
  const risk = kpiRiskLevel(kpi);
  const colors = { 'on-track': '#10B981', 'at-risk': '#F59E0B', 'critical': '#EF4444' };
  return (
    <div className="bb-dn-kpi-row">
      <div className="bb-dn-kpi-meta">
        <span className="bb-dn-kpi-label">{kpi.label}</span>
        <span className="bb-dn-kpi-value">{kpi.current ?? 0} / {kpi.target} {kpi.unit}</span>
      </div>
      <div className="bb-dn-kpi-bar-bg">
        <div className="bb-dn-kpi-bar-fill" style={{ width: `${pct}%`, background: colors[risk] }} />
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 2 }}>
        <span style={{ fontSize: 11, color: colors[risk], fontWeight: 600 }}>{pct}%</span>
        {kpi.dueDate && <span style={{ fontSize: 11, color: '#9CA3AF' }}>Due {fmtDate(kpi.dueDate instanceof Date ? { toDate: () => kpi.dueDate } : kpi.dueDate)}</span>}
      </div>
    </div>
  );
}

// ── Grant Status badge ────────────────────────────────────────────────────────
function GrantStatusBadge({ value }) {
  const s = GRANT_STATUSES.find(x => x.value === value) || { label: value, color: '#6B7280' };
  return (
    <span className="bb-dn-badge" style={{ background: s.color + '22', color: s.color, border: `1px solid ${s.color}44` }}>
      {s.label}
    </span>
  );
}

// ── KPI Editor ────────────────────────────────────────────────────────────────
function KpiEditor({ kpis, onChange }) {
  const addKpi = () => onChange([...kpis, { label: '', target: '', current: 0, unit: '', dueDate: '' }]);
  const removeKpi = idx => onChange(kpis.filter((_, i) => i !== idx));
  const update = (idx, k, v) => {
    const next = [...kpis];
    next[idx] = { ...next[idx], [k]: v };
    onChange(next);
  };

  return (
    <div className="bb-dn-kpi-editor">
      <div className="bb-dn-kpi-editor-header">
        <strong>KPIs</strong>
        <button className="bb-dn-btn-link" type="button" onClick={addKpi}>+ Add KPI</button>
      </div>
      {kpis.map((kpi, idx) => (
        <div key={idx} className="bb-dn-kpi-edit-row">
          <input className="bb-dn-input" placeholder="Label (e.g. Beneficiaries)" value={kpi.label} onChange={e => update(idx, 'label', e.target.value)} />
          <input className="bb-dn-input" placeholder="Target" type="number" value={kpi.target} onChange={e => update(idx, 'target', Number(e.target.value))} />
          <input className="bb-dn-input" placeholder="Current" type="number" value={kpi.current} onChange={e => update(idx, 'current', Number(e.target.value))} />
          <input className="bb-dn-input" placeholder="Unit (e.g. sessions)" value={kpi.unit} onChange={e => update(idx, 'unit', e.target.value)} />
          <input className="bb-dn-input" type="date" value={kpi.dueDate} onChange={e => update(idx, 'dueDate', e.target.value)} />
          <button className="bb-dn-action-btn bb-dn-action-btn--danger" type="button" onClick={() => removeKpi(idx)}>✕</button>
        </div>
      ))}
      {kpis.length === 0 && <p className="bb-dn-muted" style={{ fontSize: 13 }}>No KPIs added yet.</p>}
    </div>
  );
}

// ── Grant Form Modal ───────────────────────────────────────────────────────────
function GrantFormModal({ grant, orgId, donors, onClose, onSaved }) {
  const isEdit = !!grant;
  const [form, setForm] = useState({
    donorId:     grant?.donorId     || (donors[0]?.id || ''),
    title:       grant?.title       || '',
    amount:      grant?.amount      || '',
    startDate:   grant?.startDate   || '',
    endDate:     grant?.endDate     || '',
    renewalDate: grant?.renewalDate || '',
    status:      grant?.status      || 'active',
    kpis:        grant?.kpis        || [],
  });
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState('');

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.title.trim()) { setError('Grant title is required.'); return; }
    if (!form.donorId)      { setError('Please select a donor.'); return; }
    setSaving(true); setError('');
    try {
      const payload = { ...form, amount: form.amount ? Number(form.amount) : null };
      if (isEdit) await updateDonorGrant(grant.id, payload);
      else         await createDonorGrant(orgId, payload);
      onSaved();
    } catch (e) {
      setError(e.message || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bb-dn-modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="bb-dn-modal bb-dn-modal--wide">
        <div className="bb-dn-modal-header">
          <h3>{isEdit ? 'Edit Grant' : 'Add Grant'}</h3>
          <button className="bb-dn-modal-close" onClick={onClose}>✕</button>
        </div>

        {error && <div className="bb-dn-alert bb-dn-alert--error">{error}</div>}

        <div className="bb-dn-form-grid">
          <div className="bb-dn-form-group bb-dn-full">
            <label>Donor Organisation <span className="bb-dn-required">*</span></label>
            <select className="bb-dn-select" value={form.donorId} onChange={e => set('donorId', e.target.value)}>
              <option value="">— Select donor —</option>
              {donors.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          </div>

          <div className="bb-dn-form-group bb-dn-full">
            <label>Grant Title <span className="bb-dn-required">*</span></label>
            <input className="bb-dn-input" value={form.title} onChange={e => set('title', e.target.value)} placeholder="e.g. Community Support Grant 2025" />
          </div>

          <div className="bb-dn-form-group">
            <label>Amount (SGD)</label>
            <input className="bb-dn-input" type="number" value={form.amount} onChange={e => set('amount', e.target.value)} placeholder="0" />
          </div>

          <div className="bb-dn-form-group">
            <label>Status</label>
            <select className="bb-dn-select" value={form.status} onChange={e => set('status', e.target.value)}>
              {GRANT_STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </div>

          <div className="bb-dn-form-group">
            <label>Start Date</label>
            <input className="bb-dn-input" type="date" value={form.startDate} onChange={e => set('startDate', e.target.value)} />
          </div>

          <div className="bb-dn-form-group">
            <label>End Date</label>
            <input className="bb-dn-input" type="date" value={form.endDate} onChange={e => set('endDate', e.target.value)} />
          </div>

          <div className="bb-dn-form-group">
            <label>Renewal Date</label>
            <input className="bb-dn-input" type="date" value={form.renewalDate} onChange={e => set('renewalDate', e.target.value)} />
          </div>

          <div className="bb-dn-form-group bb-dn-full">
            <KpiEditor kpis={form.kpis} onChange={v => set('kpis', v)} />
          </div>
        </div>

        <div className="bb-dn-modal-footer">
          <button className="bb-dn-btn bb-dn-btn-secondary" onClick={onClose}>Cancel</button>
          <button className="bb-dn-btn bb-dn-btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Add Grant'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Grant Card ────────────────────────────────────────────────────────────────
function GrantCard({ grant, donorName, onEdit, onDelete }) {
  const [expanded, setExpanded] = useState(false);
  const renewDays = grant.renewalDate ? daysUntil({ toDate: () => new Date(grant.renewalDate) }) : null;
  const atRiskKpis = (grant.kpis || []).filter(k => kpiRiskLevel(k) !== 'on-track');

  return (
    <div className="bb-dn-grant-card">
      <div className="bb-dn-grant-card-header">
        <div>
          <div className="bb-dn-grant-title">{grant.title}</div>
          <div className="bb-dn-grant-donor">{donorName}</div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <GrantStatusBadge value={grant.status} />
          <button className="bb-dn-action-btn" onClick={onEdit}>Edit</button>
          <button className="bb-dn-action-btn bb-dn-action-btn--danger" onClick={onDelete}>Delete</button>
        </div>
      </div>

      <div className="bb-dn-grant-meta">
        {grant.amount && <span>💰 SGD {Number(grant.amount).toLocaleString()}</span>}
        {grant.startDate && <span>Start: {fmtDate({ toDate: () => new Date(grant.startDate) })}</span>}
        {grant.endDate   && <span>End: {fmtDate({ toDate: () => new Date(grant.endDate) })}</span>}
        {renewDays !== null && (
          <span className={renewDays <= 30 ? 'bb-dn-warn-text' : ''}>
            🔁 Renewal: {renewDays > 0 ? `in ${renewDays}d` : 'overdue'}
          </span>
        )}
        {atRiskKpis.length > 0 && (
          <span className="bb-dn-alert-pill">⚠ {atRiskKpis.length} KPI{atRiskKpis.length > 1 ? 's' : ''} at risk</span>
        )}
      </div>

      {(grant.kpis || []).length > 0 && (
        <button className="bb-dn-btn-link" onClick={() => setExpanded(v => !v)}>
          {expanded ? 'Hide KPIs ▲' : `Show ${grant.kpis.length} KPI${grant.kpis.length > 1 ? 's' : ''} ▼`}
        </button>
      )}

      {expanded && (
        <div className="bb-dn-kpi-list">
          {grant.kpis.map((kpi, i) => <KpiBar key={i} kpi={kpi} />)}
        </div>
      )}
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────────
export default function DonorGrants({ orgId, filterDonorId = null }) {
  const [grants,  setGrants]  = useState([]);
  const [donors,  setDonors]  = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [editGrant, setEditGrant] = useState(null);
  const [deleteItem, setDeleteItem] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [g, d] = await Promise.all([
        getDonorGrants(orgId, filterDonorId),
        getDonors(orgId),
      ]);
      setGrants(g); setDonors(d);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [orgId, filterDonorId]);

  useEffect(() => { load(); }, [load]);

  const filtered = grants.filter(g => statusFilter === 'all' || g.status === statusFilter);
  const getDonorName = id => donors.find(d => d.id === id)?.name || '—';

  const handleDelete = async () => {
    setDeleting(true);
    try { await deleteDonorGrant(deleteItem.id); setDeleteItem(null); load(); }
    catch (e) { console.error(e); }
    finally { setDeleting(false); }
  };

  // KPI summary stats
  const totalGrants = grants.length;
  const atRiskCount = grants.filter(g => (g.kpis || []).some(k => kpiRiskLevel(k) !== 'on-track')).length;
  const totalValue  = grants.reduce((sum, g) => sum + (Number(g.amount) || 0), 0);

  if (loading) return <div className="bb-dn-loading"><div className="bb-loading-spinner" /><p>Loading grants…</p></div>;

  return (
    <div className="bb-dn-section">
      <div className="bb-dn-section-header">
        <div>
          <h2 className="bb-dn-section-title">Grants & KPI Tracker</h2>
          <p className="bb-dn-section-subtitle">Monitor grant conditions and KPI compliance</p>
        </div>
        <button className="bb-dn-btn bb-dn-btn-primary" onClick={() => { setEditGrant(null); setShowForm(true); }}>
          + Add Grant
        </button>
      </div>

      {/* Summary stats */}
      <div className="bb-dn-stats-row">
        <div className="bb-dn-stat-card">
          <div className="bb-dn-stat-value">{totalGrants}</div>
          <div className="bb-dn-stat-label">Total Grants</div>
        </div>
        <div className="bb-dn-stat-card">
          <div className="bb-dn-stat-value">SGD {totalValue.toLocaleString()}</div>
          <div className="bb-dn-stat-label">Total Value</div>
        </div>
        <div className="bb-dn-stat-card" style={{ borderColor: atRiskCount > 0 ? '#F59E0B' : undefined }}>
          <div className="bb-dn-stat-value" style={{ color: atRiskCount > 0 ? '#F59E0B' : undefined }}>{atRiskCount}</div>
          <div className="bb-dn-stat-label">Grants with KPIs at Risk</div>
        </div>
      </div>

      <div className="bb-dn-filters">
        <select className="bb-dn-select" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
          <option value="all">All Statuses</option>
          {GRANT_STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
        <span className="bb-dn-count">{filtered.length} grant{filtered.length !== 1 ? 's' : ''}</span>
      </div>

      {filtered.length === 0 ? (
        <div className="bb-dn-empty">
          <div className="bb-dn-empty-icon">📋</div>
          <p>{grants.length === 0 ? 'No grants added yet.' : 'No grants match your filters.'}</p>
        </div>
      ) : (
        <div className="bb-dn-cards-list">
          {filtered.map(g => (
            <GrantCard
              key={g.id}
              grant={g}
              donorName={getDonorName(g.donorId)}
              onEdit={() => { setEditGrant(g); setShowForm(true); }}
              onDelete={() => setDeleteItem(g)}
            />
          ))}
        </div>
      )}

      {showForm && (
        <GrantFormModal
          grant={editGrant}
          orgId={orgId}
          donors={donors}
          onClose={() => { setShowForm(false); setEditGrant(null); }}
          onSaved={() => { setShowForm(false); setEditGrant(null); load(); }}
        />
      )}

      {deleteItem && (
        <div className="bb-dn-modal-overlay" onClick={e => e.target === e.currentTarget && setDeleteItem(null)}>
          <div className="bb-dn-modal" style={{ maxWidth: 440 }}>
            <div className="bb-dn-modal-header">
              <h3>Delete Grant</h3>
              <button className="bb-dn-modal-close" onClick={() => setDeleteItem(null)}>✕</button>
            </div>
            <p style={{ padding: '16px 0', color: 'var(--bb-gray)' }}>
              Delete <strong>{deleteItem.title}</strong>? All KPIs will be lost.
            </p>
            <div className="bb-dn-modal-footer">
              <button className="bb-dn-btn bb-dn-btn-secondary" onClick={() => setDeleteItem(null)}>Cancel</button>
              <button className="bb-dn-btn bb-dn-btn-danger" onClick={handleDelete} disabled={deleting}>
                {deleting ? 'Deleting…' : 'Delete Grant'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
